import * as angular from "angular";
import * as _ from "lodash";
import { FileUploader, FileUploaderFactory } from "angular-file-upload";
import { Field } from "../models/field.model";
import { VoterFileImport } from "../models/voter-file-import.model";
import { VoterFileService } from "../services/voter-file.service";
import { CredentialsManager } from "../shared/credentials-manager";
import { DialogUtils } from "../helper/dialog.helper";
import { ToastUtils } from "../helper/toast.helper";
import { FieldDataProvider } from "./field.dp";
import { UserDataProvider } from "./user.dp";

export class VoterFileDataProvider {
    private readonly $q: any;
    private readonly service: VoterFileService;
    private readonly $timeout: any;
    private $window: any;
    private readonly $route: any;
    private readonly modelsUtils: any;
    private readonly dialog: DialogUtils;
    private readonly toast: ToastUtils;

    private readonly fileUploader: FileUploaderFactory;
    private credentialsManager: CredentialsManager;
    private userDataProvider: UserDataProvider;
    private fieldDataProvider: FieldDataProvider;

    public voterFiles: any[] | null = null;
    public fields: any[] | null = null;
    public getVoterFilePromise: Promise<any> | null = null;
    public uploader: FileUploader;
    public isLeaderView: boolean;
    private importListeners: Array<
        (operation: string, socketData: any) => void
    > = [];
    public readonly aliases = {
        1: ["fname", "firstname", "forename", "votersfirstname"],
        2: ["lastname", "surname", "secondname", "voterslastname"],
        3: ["dob", "dateofbirth", "votersbirthdate"],
        4: [
            "houseunit",
            "unit",
            "unitnumber",
            "apartment",
            "apartmentnumber",
            "residenceaddressesapartmentnum",
            "lägenhetsnummer",
        ],
        5: [
            "hnumber",
            "housenumber",
            "number",
            "residenceaddresseshousenumber",
            "gatunummer/bokstav",
            "gatunummer",
            "bokstav",
        ],
        6: [
            "street",
            "streetname",
            "streetaddress",
            "propertyaddress",
            "addressstreet",
            "residenceaddressesstreetname",
            "gata",
        ],
        7: [
            "city",
            "town",
            "propertycity",
            "addresscity",
            "residenceaddressescity",
            "postort",
        ],
        8: [
            "state",
            "addressstate",
            "propertystate",
            "county",
            "countyname",
            "residenceaddressesstate",
            "kommun",
        ],
        9: [
            "zip",
            "zipcode",
            "postcode",
            "eircode",
            "residenceaddresseszip",
            "postnummer",
        ],
        10: ["precinct", "precinctname", "valdistrikt"],
        11: ["gender", "sex", "votersgender"],
        12: ["party", "politicalparty", "partiesdescription"],
        13: ["email", "emailaddress"],
        14: ["homephone", "homenumber", "votertelephoneslandlineformatted"],
        15: [
            "mobilephone",
            "mobile",
            "mobilenumber",
            "mob",
            "votertelephonescellphoneformatted",
        ],
        16: ["voterid", "lalvoterid", "contactid", "unikidlghnummer"],
        17: ["middlename", "votersmiddlename"],
        20: ["volunteer"],
        22: ["deceased"],
        23: ["housename"],
        25: ["longitude", "long", "x", "residenceaddresseslongitude"],
        26: ["latitude", "lat", "y", "residenceaddresseslatitude"],
        31: ["suffix", "votersnamesuffix"],
        32: [
            "streetdirection",
            "postdirection",
            "directionalsuffix",
            "directionalsuffixpost",
            "residenceaddressessuffixdirection",
        ],
        33: ["streettype", "streetdesignator"],
        35: ["precinctnumber", "valdistriktskod"],
        36: ["unittype", "residenceaddressesapartmenttype"],
        37: [
            "residenceaddressesprefixdirection",
            "directionalprefix",
            "directionalprefixpre",
        ],
        38: ["donor"],
        41: ["floor"],
        42: ["direction", "directions"],
        43: ["buildingnotes"],
        44: ["buildingaccessibility", "accesstype"],
        45: ["housenotes"],
        46: ["salary", "income", "wages"],
        47: ["creditrating", "creditscore"],
        48: ["greenliving", "interestedingreenliving", "environmentaldonor"],
        49: ["dwellingtype", "dwelling"],
        50: ["homeowner", "ownshome", "houseowner", "ownshouse"],
        51: [
            "squarefootage",
            "sqfootage",
            "homesquarefootage",
            "homesqfootage",
        ],
        52: ["yearbuilt", "yearcompleted", "yearcontructed", "buildyear"],
        53: ["purchaseyear", "yearpurchased"],
        54: ["purchaseprice", "houseprice"],
        55: ["networth", "householdnetworth"],
        56: ["addressid", "houseid", "addressuniqueid", "houseuniqueid"],
    };

    constructor(
        $q: any,
        VoterFileService: VoterFileService,
        $timeout: any,
        $window: any,
        FileUploader: FileUploaderFactory,
        ToastUtils: ToastUtils,
        CredentialsManager: CredentialsManager,
        UserDataProvider: UserDataProvider,
        FieldDataProvider: FieldDataProvider,
        __app_type: string,
        $route: any,
        DialogUtils: DialogUtils,
        ModelsUtils: any
    ) {
        this.$q = $q;
        this.service = VoterFileService;
        this.$timeout = $timeout;
        this.$window = $window;
        this.fileUploader = FileUploader;
        this.toast = ToastUtils;
        this.credentialsManager = CredentialsManager;
        this.userDataProvider = UserDataProvider;
        this.fieldDataProvider = FieldDataProvider;
        this.$route = $route;
        this.dialog = DialogUtils;
        this.modelsUtils = ModelsUtils;

        this.isLeaderView = __app_type === "LEADER";
    }

    columnForAlias(
        alias: any,
        customfields: Field[],
        elections: any[],
        importDataType: string = "all"
    ): any {
        var aliases: { [key: number]: string[] };
        if (importDataType !== "all") {
            aliases = this.getFilteredAliases(importDataType);
        } else {
            aliases = _.clone(this.aliases);
        }
        if (typeof alias === "string") {
            let trimmedAlias = alias.trim();

            let formattedAlias = alias
                .toLowerCase()
                .replace(/\s+|_/g, "") //Replaces any white space and underscores with empty string
                .replace(/[\[\]']+/g, ""); //Replaces any square brackets
            for (let columnid in aliases) {
                if (aliases.hasOwnProperty(columnid)) {
                    for (let i = 0; i < aliases[columnid].length; i++) {
                        if (formattedAlias === aliases[columnid][i]) {
                            return this.columnWithID(columnid);
                        }
                    }
                }
            }
            if (importDataType !== "houses") {
                for (let i = 0; i < customfields.length; i++) {
                    if (customfields[i].name === trimmedAlias)
                        return customfields[i];
                }
                for (let i = 0; i < elections.length; i++) {
                    if (elections[i].elname === trimmedAlias)
                        return elections[i];
                }
            }
        }
        return null;
    }

    columnWithID(columnid: string | number): object | null {
        for (let i = 0; i < this.fields.length; i++) {
            for (let j = 0; j < this.fields[i].columns.length; j++) {
                if (this.fields[i].columns[j].columnid == columnid) {
                    return this.fields[i].columns[j];
                }
            }
        }
        return null;
    }

    public getFilteredAliases(importDataType: string) {
        const allowedHousesKeys = new Set<number>();

        // Populate allowed keys for houses
        for (let i = 4; i <= 10; i++) allowedHousesKeys.add(i);
        for (let i = 23; i <= 26; i++) allowedHousesKeys.add(i);
        for (let i = 31; i <= 37; i++) allowedHousesKeys.add(i);
        for (let i = 41; i <= 45; i++) allowedHousesKeys.add(i);
        allowedHousesKeys.add(49);
        for (let i = 51; i <= 55; i++) allowedHousesKeys.add(i);

        const filteredAliases: { [key: number]: string[] } = {};

        for (const keyStr in this.aliases) {
            const key = parseInt(keyStr, 10);
            if (isNaN(key)) continue;

            if (importDataType === "houses") {
                if (allowedHousesKeys.has(key)) {
                    filteredAliases[key] = this.aliases[key];
                }
            } else if (importDataType === "contacts") {
                if (!allowedHousesKeys.has(key)) {
                    filteredAliases[key] = this.aliases[key];
                }
            }
        }

        return filteredAliases;
    }

    getVoterFileUploader(): FileUploader {
        if (this.uploader) return this.uploader;
        let url = this.isLeaderView
            ? "/api/organisation/voterfile/?oid=" +
              this.credentialsManager.oid +
              "&campaignid=" +
              this.credentialsManager.campaignid
            : "/api/voterfile/?campaignid=" +
              this.credentialsManager.campaignid;

        this.uploader = new this.fileUploader({
            url: url,
            headers: {
                Authorization:
                    "Bearer " + this.credentialsManager.getAccessToken(),
            } as any,
            autoUpload: true,
            removeAfterUpload: true,
        });

        this.uploader.onCompleteItem = (
            fileItem: any,
            response: any,
            status: any,
            headers: any
        ): void => {
            if (angular.isUndefined(response.errors)) {
                let voterfile = this.modelsUtils.import(response["data"]);
                this.replaceVoterFile(voterfile);
                this.toast.success(
                    "File uploading, this may take a few minutes. You will be notified once ready to import."
                );
            } else {
                let message: string | undefined;
                let error = response.errors[0] ?? null;

                if (error && error.title && error.detail) {
                    message = error.title + " - " + error.detail;
                }
                this.toast.error(message);

                this.clearAll();
                this.$route.reload();
            }
        };

        this.uploader.onBeforeUploadItem = (item: any): void => {
            item.contentStatus = true;
        };

        this.uploader.filters.push({
            name: "customFilter",
            fn: (
                item: any /*{File|FileLikeObject}*/,
                options: any
            ): boolean => {
                // this.queue = [];
                let ext = item.name.substr(item.name.lastIndexOf(".") + 1);
                // Maximum file size is 500MB
                return (
                    "|csv|xlsx|xls|CSV|XLSX|XLS|".indexOf(ext) !== -1 &&
                    item.size <= 524288000
                );
            },
        });

        return this.uploader;
    }

    showError(register: VoterFileImport): void {
        if (register.readError()) {
            let errorModal = this.dialog
                .alert()
                .title("Error")
                .htmlContent(
                    "<span>There was an issue reading this file. For .xlsx files, try converting to a .csv file then re-upload the converted file. Please contact support should you continue to experience issues with your file.</span>"
                )
                .ok("OK");
            this.dialog.showPreConfiguredDialog(errorModal);
        } else if (register.limitError()) {
            this.dialog
                .show({
                    component: "vc-import-error-dialog",
                    skipHide: true,
                    fullscreen: false,
                    clickOutsideToClose: true,
                    araiLabel: "Contact/House Database Limit Exceeded",
                    style: "max-width: 550px",
                })
                .then(() => {
                    if (this.isLeaderView) {
                        this.$window.location = `./leader.php/#/leader_voterfiles/import/${register.erid}`;
                    } else {
                        this.$window.location = `./#/voterfiles/import/${register.erid}`;
                    }
                });
        } else if (register.sizeError()) {
            let sizeErrorModal = this.dialog
                .alert()
                .title("File Processing Error")
                .htmlContent(
                    "<span>Please check that the file size does not exceed 40MB and does not have more than 4,000 columns.</span>"
                )
                .ok("OK");
            this.dialog.showPreConfiguredDialog(sizeErrorModal);
        }
    }

    leaderImportProgress(imid: string | number): Promise<any> {
        const deferred = this.$q.defer();
        this.service.leaderImportProgress({ id: imid }).then(
            (response: object) => {
                const json = response["data"];
                if (angular.isDefined(json.data)) {
                    deferred.resolve(json.data);
                } else {
                    deferred.reject();
                }
            },
            (error: any) => deferred.reject(error)
        );
        return deferred.promise;
    }

    getCampaignColumnValues(erid: string | number, column: any): Promise<any> {
        const deferred = this.$q.defer();
        this.service.getCampaignColumnValues({ id: erid, column: column }).then(
            (response: object) => {
                const json = response["data"];
                if (angular.isDefined(json.data)) {
                    deferred.resolve(json.data);
                } else {
                    deferred.reject();
                }
            },
            (error: any) => deferred.reject(error)
        );
        return deferred.promise;
    }

    info(id: string | number): Promise<any> {
        const deferred = this.$q.defer();
        this.service.info({ id: id }).then(
            (response: object) => {
                const json = response["data"];
                if (angular.isDefined(json.data)) {
                    deferred.resolve(this.modelsUtils.import(json.data));
                } else {
                    deferred.reject();
                }
            },
            (error: any) => deferred.reject(error)
        );
        return deferred.promise;
    }

    importInfo(id: string | number): Promise<any> {
        const deferred = this.$q.defer();
        this.service.importInfo({ id: id }).then(
            (response: object) => {
                const json = response["data"];
                if (angular.isDefined(json.data)) {
                    let data = json.data;
                    data = _.merge(data.file, data.creator, data);
                    deferred.resolve(this.modelsUtils.import(data));
                } else {
                    deferred.reject();
                }
            },
            (error: any) => deferred.reject(error)
        );
        return deferred.promise;
    }

    getVoterFiles(): Promise<any> {
        const deferred = this.$q.defer();
        if (this.voterFiles !== null) {
            deferred.resolve(this.voterFiles);
            this.getVoterFilePromise = deferred.promise;
            return this.getVoterFilePromise;
        } else if (this.getVoterFilePromise) {
            return this.getVoterFilePromise;
        } else {
            this.service.user_registers({}).then(
                (response: object) => {
                    const json = response["data"];
                    if (angular.isDefined(json.data)) {
                        this.voterFiles = [];
                        for (let i = 0; i < json.data.length; i++) {
                            this.voterFiles.unshift(
                                this.modelsUtils.import(json.data[i])
                            );
                        }
                        deferred.resolve(this.voterFiles);
                    } else if (angular.isDefined(json.errors)) {
                        deferred.reject(json.errors);
                    }
                },
                (error: any) => {
                    this.voterFiles = [];
                    deferred.reject(error);
                }
            );
        }
        this.getVoterFilePromise = deferred.promise;
        return deferred.promise;
    }

    getFields(): Promise<any> {
        const deferred = this.$q.defer();
        if (this.fields === null) {
            this.service.columns().then(
                (response: object) => {
                    const json = response["data"];
                    this.fields = json.data;
                    deferred.resolve(this.fields);
                },
                (error: any) => {
                    this.fields = [];
                    deferred.reject(error);
                }
            );
        } else {
            deferred.resolve(this.fields);
        }
        return deferred.promise;
    }

    clearAll(): void {
        this.getVoterFilePromise = null;
        this.uploader = null;
        this.voterFiles = null;
    }

    clearVoterFiles(): void {
        this.voterFiles = null;
    }

    stats(erid: string | number): Promise<any> {
        const deferred = this.$q.defer();
        this.service.stats({ id: erid }).then(
            (response: object) => {
                const json = response["data"];
                deferred.resolve(json.data);
            },
            (error: any) => deferred.reject(error)
        );
        return deferred.promise;
    }
    skippedCount(imid: string | number): Promise<any> {
        const deferred = this.$q.defer();
        this.service.skippedRowsCount({ id: imid }).then(
            (response: object) => {
                const json = response["data"];
                deferred.resolve(json.data);
            },
            (error: any) => deferred.reject(error)
        );
        return deferred.promise;
    }

    updateImport(importObj: VoterFileImport): Promise<any> {
        const deferred = this.$q.defer();
        this.service.updateImport(importObj).then(
            (response: object) => {
                const json = response["data"];
                deferred.resolve(json);
            },
            (error: any) => deferred.reject(error)
        );
        return deferred.promise;
    }

    mapping(imid: string | number): Promise<any> {
        const deferred = this.$q.defer();
        this.service.mapping({ id: imid }).then(
            (response: object) => {
                const json = response["data"];
                deferred.resolve(json);
            },
            (error: any) => deferred.reject(error)
        );
        return deferred.promise;
    }

    replaceVoterFile(voterFile: VoterFileImport): void {
        this.$timeout(() => {
            let existingVoterFile = this.voterFileWithID(
                voterFile.erid,
                voterFile.imid
            );
            if (existingVoterFile) {
                let wasInProgress = existingVoterFile.inProgress();
                existingVoterFile.setData(voterFile);
                let nowInProgress = existingVoterFile.inProgress();
                if (wasInProgress && !nowInProgress) {
                    //has changed from in Progress to not. Keep in queue but now popover is closeable.
                    this.fieldDataProvider.clearAll();
                }

                let index = this.voterFileIndex(voterFile.erid, voterFile.imid);
                this.voterFiles[index] = voterFile;
            } else {
                if (this.voterFiles && voterFile.erid) {
                    this.voterFiles.unshift(this.modelsUtils.import(voterFile));
                }
            }
        });
    }

    deleteImportData(imid: string | number): Promise<any> {
        const deferred = this.$q.defer();
        this.service.delete({ id: imid }).then(
            (response: object) => {
                const json = response["data"];
                if (angular.isUndefined(json.error)) {
                    deferred.resolve(this.modelsUtils.import(json.data));
                } else {
                    deferred.reject(json);
                }
            },
            (error: any) => deferred.reject(error?.data)
        );
        return deferred.promise;
    }

    voterFileIndex(
        erid: string | number,
        imid: string | number
    ): number | null {
        if (this.voterFiles && erid) {
            if (imid) {
                for (let i = 0; i < this.voterFiles.length; i++) {
                    if (this.voterFiles[i].imid == imid) {
                        return i;
                    }
                }
            }
            for (let x = 0; x < this.voterFiles.length; x++) {
                if (
                    this.voterFiles[x].erid == erid &&
                    !this.voterFiles[x].imid
                ) {
                    return x;
                }
            }
        }
        return null;
    }

    voterFileWithID(
        erid: string | number,
        imid: string | number
    ): VoterFileImport {
        if (this.voterFiles && erid) {
            if (imid) {
                for (let i = 0; i < this.voterFiles.length; i++) {
                    if (this.voterFiles[i].imid == imid) {
                        return this.voterFiles[i];
                    }
                }
            }
            for (let x = 0; x < this.voterFiles.length; x++) {
                if (
                    this.voterFiles[x].erid == erid &&
                    !this.voterFiles[x].imid
                ) {
                    return this.voterFiles[x];
                }
            }
        }
        return null;
    }

    getColumnsData(erid: string | number): Promise<any> {
        const deferred = this.$q.defer();
        this.service.getColumnsData(erid).then(
            (response: object) => {
                const json = response["data"];
                if (angular.isDefined(json.data)) {
                    deferred.resolve(json.data);
                } else {
                    deferred.reject();
                }
            },
            (error: any) => deferred.reject(error)
        );
        return deferred.promise;
    }

    processSocketUpdate(socketData: any): void {
        let voterfile = this.modelsUtils.import(socketData);

        if (this.voterFiles) {
            let oldVoterFile = this.voterFileWithID(
                voterfile.erid,
                voterfile.imid
            );

            if (
                oldVoterFile &&
                oldVoterFile.uid == this.userDataProvider.getActiveUser().uid &&
                oldVoterFile.imsid == 2 &&
                voterfile.imsid == 3
            ) {
                this.dialog
                    .delete({
                        skipHide: true,
                        locals: {
                            bodyText:
                                "We have finished reading your contact file:  " +
                                oldVoterFile.name() +
                                " Would you like to start the import now?",
                            title: "File Reading Finished",
                        },
                    })
                    .then(() => {
                        if (this.isLeaderView) {
                            this.$window.location =
                                "/leader.php#/leader_voterfiles/import/" +
                                voterfile.erid;
                        } else {
                            this.$window.location =
                                "/#/voterfiles/import/" + voterfile.erid;
                        }
                    });
            }
            this.replaceVoterFile(voterfile);
        }
        this.notifyImportListeners("update", socketData);
    }

    processSocketCreate(socketData: any): void {
        if (this.voterFiles) {
            let voterfile = this.modelsUtils.import(socketData);
            this.replaceVoterFile(voterfile);
        }
    }

    processSocketDelete(socketData: any): void {
        if (this.voterFiles) {
            let voterfile = this.voterFileWithID(
                socketData.erid,
                socketData.imid
            );
            if (voterfile) {
                voterfile.status = "Deleted";
                this.replaceVoterFile(voterfile);
            }
        }
        this.notifyImportListeners("delete", socketData);
    }

    /**
     * Lets a page holding its own copy of an import (the info page) follow
     * socket changes to it. Returns a function that removes the listener.
     */
    onImportSocket(
        listener: (operation: string, socketData: any) => void
    ): () => void {
        this.importListeners.push(listener);
        return () => {
            this.importListeners = this.importListeners.filter(
                (l) => l !== listener
            );
        };
    }

    private notifyImportListeners(operation: string, socketData: any): void {
        this.importListeners.forEach((listener) =>
            listener(operation, socketData)
        );
    }
}

angular
    .module("vcDataProvider")
    .service("VoterFileDataProvider", VoterFileDataProvider);
